import { ROLES } from 'app/auth';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { createSelector } from 'reselect';

import { LoanContactTypeEnum } from 'constants/loanContactTypes';
import searchItems from 'utils/searchItems';

import { getIsFromInternalCompany } from 'store/auth/selectors';
import { selectRolesList } from 'store/global/selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

import { getSystem } from './system.selectors';

export const selectLoanRoles = createSelector(getSystem, ({ loanRoles }) => loanRoles);

export const selectLoanRolesValues = createSelector(selectLoanRoles, Object.values);

const selectExistingLoanRolesValues = createSelector(selectLoanRolesValues, values =>
  values.filter(role => !role.isDeleted),
);

export const selectLoanRolesOptions = createSelector(selectLoanRolesValues, values =>
  values.map(loanRole => ({
    name: loanRole.name,
    value: loanRole.loanRoleId,
    isGlobal: checkIsInternalCompany(loanRole),
  })),
);

export const selectExistingLoanRolesOptions = createSelector(
  selectExistingLoanRolesValues,
  values =>
    values.map(loanRole => ({
      name: loanRole.name,
      value: loanRole.loanRoleId,
      isGlobal: checkIsInternalCompany(loanRole),
    })),
);

const selectSortedLoanRolesOptions = createSelector(selectLoanRolesOptions, loanRolesOptions =>
  loanRolesOptions.sort((a, b) => (a.name > b.name ? 1 : -1)),
);

export const selectFormattedLoanRolesOptions = createSelector(
  selectSortedLoanRolesOptions,
  loanRolesOptions =>
    loanRolesOptions.map(option => ({
      ...option,
      name: `Loan Role: ${option.name}`,
      value: LoanContactTypeEnum.LoanRole,
      loanRoleId: option.value,
    })),
);

const selectExtendedLoanRolesList = createSelector(
  [selectLoanRolesValues, getIsFromInternalCompany],
  (loanRoles, isMasterFromInternalCompany) =>
    loanRoles.map(loanRole => {
      const isGlobalRole = checkIsInternalCompany(loanRole);

      return {
        ...loanRole,
        isGlobal: isGlobalRole,
        isReadOnly: isGlobalRole && !isMasterFromInternalCompany,
        userRoles: (loanRole.userRoles || []).map(userRole => ({
          ...userRole,
          isGlobal: checkIsInternalCompany(userRole),
        })),
      };
    }),
);

export const selectEligibleUserRoles = createSelector(
  [selectRolesList, getIsFromInternalCompany],
  (userRolesList, isMasterFromInternalCompany) =>
    userRolesList.reduce((acc, userRole) => {
      const isAdminRole =
        userRole?.name?.toLowerCase() === ROLES.ADMIN && !isMasterFromInternalCompany;

      if (!isAdminRole) {
        acc.push({
          isGlobal: checkIsInternalCompany(userRole),
          ...userRole,
        }); // TODO: remove isGlobal flag calculation after moving to new API completely
      }

      return acc;
    }, []),
);

export const selectFilteredLoanRoles = createSelector(
  getDebouncedSearchValue,
  selectExtendedLoanRolesList,
  searchItems(({ name }) => [name]),
);
